import React, { useRef, useState } from "react";
import { supabase } from "../supabaseClient";

const ACCEPTED_TYPES = ["image/png", "image/jpeg", "image/webp"];
const MAX_SIZE_MB = 5;

/**
 * Komponen upload gambar produk.
 * Props:
 * - value: string (image_url saat ini, bisa null/empty)
 * - onChange: (newUrl: string|null) => void
 * - productId: string (dipakai untuk membentuk nama file unik di Storage)
 */
export default function ImageUploadField({ value, onChange, productId }) {
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const [dragOver, setDragOver] = useState(false);
  const [showManualUrl, setShowManualUrl] = useState(false);

  const validateFile = (file) => {
    if (!ACCEPTED_TYPES.includes(file.type)) {
      return "Format file harus PNG, JPG, atau WebP.";
    }
    if (file.size > MAX_SIZE_MB * 1024 * 1024) {
      return `Ukuran file maksimal ${MAX_SIZE_MB}MB.`;
    }
    return null;
  };

  const uploadFile = async (file) => {
    const validationError = validateFile(file);
    if (validationError) {
      setError(validationError);
      return;
    }
    setError("");
    setUploading(true);

    try {
      const ext = file.name.split(".").pop().toLowerCase();
      const safeProductId = (productId || "product").trim() || "product";
      // Nama file unik per produk + timestamp, supaya tidak konflik dengan
      // upload sebelumnya dan browser tidak men-cache versi lama.
      const fileName = `${safeProductId}-${Date.now()}.${ext}`;

      const { error: uploadErr } = await supabase.storage
        .from("product-assets")
        .upload(fileName, file, { upsert: true, contentType: file.type });

      if (uploadErr) {
        setError(`Gagal upload: ${uploadErr.message}`);
        setUploading(false);
        return;
      }

      const { data: publicUrlData } = supabase.storage
        .from("product-assets")
        .getPublicUrl(fileName);

      onChange(publicUrlData.publicUrl);
    } catch (err) {
      setError(`Error: ${err.message}`);
    } finally {
      setUploading(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) uploadFile(file);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) uploadFile(file);
  };

  const handleRemove = () => {
    onChange(null);
    setError("");
  };

  return (
    <div>
      {value ? (
        // ── Preview gambar yang sudah ada ──
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <div style={{
            width: 64, height: 64, borderRadius: 8, background: "#f1f5f9",
            border: "1px solid #e2e8f0", display: "flex", alignItems: "center",
            justifyContent: "center", overflow: "hidden", flexShrink: 0,
          }}>
            <img src={value} alt="" style={{ width: "85%", height: "85%", objectFit: "contain" }} />
          </div>
          <div style={{ flex: 1 }}>
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              style={smallBtnStyle("#3b82f6", "#eff6ff")}
            >
              {uploading ? "Mengupload..." : "Ganti Gambar"}
            </button>
            <button
              type="button"
              onClick={handleRemove}
              style={{ ...smallBtnStyle("#ef4444", "#fef2f2"), marginLeft: 8 }}
            >
              Hapus
            </button>
          </div>
        </div>
      ) : (
        // ── Drop zone kosong ──
        <div
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={handleDrop}
          onClick={() => fileInputRef.current?.click()}
          style={{
            border: `2px dashed ${dragOver ? "#3b82f6" : "#cbd5e1"}`,
            borderRadius: 10, padding: "24px 16px", textAlign: "center",
            cursor: "pointer", background: dragOver ? "#eff6ff" : "#f8fafc",
            transition: "all 0.15s",
          }}
        >
          {uploading ? (
            <div style={{ fontSize: 13, color: "#3b82f6", fontWeight: 600 }}>Mengupload...</div>
          ) : (
            <>
              <div style={{ fontSize: 13, color: "#475569", fontWeight: 600, marginBottom: 4 }}>
                Klik atau tarik gambar ke sini
              </div>
              <div style={{ fontSize: 11, color: "#94a3b8" }}>
                PNG, JPG, atau WebP — maksimal {MAX_SIZE_MB}MB
              </div>
            </>
          )}
        </div>
      )}

      <input
        ref={fileInputRef}
        type="file"
        accept={ACCEPTED_TYPES.join(",")}
        onChange={handleFileSelect}
        style={{ display: "none" }}
      />

      {error && (
        <div style={{ color: "#dc2626", fontSize: 12, marginTop: 6 }}>{error}</div>
      )}

      <button
        type="button"
        onClick={() => setShowManualUrl((v) => !v)}
        style={{
          background: "none", border: "none", color: "#94a3b8", fontSize: 11,
          textDecoration: "underline", cursor: "pointer", padding: 0, marginTop: 8,
        }}
      >
        {showManualUrl ? "Sembunyikan input URL manual" : "atau isi URL gambar secara manual"}
      </button>

      {showManualUrl && (
        <input
          value={value || ""}
          onChange={(e) => onChange(e.target.value || null)}
          placeholder="https://... atau path URL gambar"
          style={{
            width: "100%", padding: "8px 10px", borderRadius: 8, border: "1px solid #cbd5e1",
            fontSize: 12, marginTop: 6, outline: "none", boxSizing: "border-box",
          }}
        />
      )}
    </div>
  );
}

function smallBtnStyle(color, bg) {
  return {
    padding: "6px 12px", borderRadius: 6, border: `1px solid ${color}33`,
    background: bg, color, fontSize: 12, fontWeight: 600, cursor: "pointer",
  };
}
